import { Controller } from '@hotwired/stimulus'

// How long the handle takes to return when it is let go short of the end.
const SPRING_BACK_MS = 260

/**
 * Staging for a deletion that cannot be undone — two screens in the one modal,
 * so nothing fires on momentum:
 *
 *   1. The disclosure: what is about to die, and the phrase typed out in full.
 *      Continue stays disabled until the phrase matches.
 *   2. The confirmation: a quote, then our own plain question, answered by
 *      carrying the handle across rather than by a click, which is turned away.
 *
 * The typed phrase travels with the form and is checked again server side
 * (StagedDeletion), so this is the staging, not the guard.
 */
export default class extends Controller {
  static targets = ['disclosure', 'confirmation', 'confirmationInput', 'continueButton', 'form', 'slide', 'slider', 'slideLabel']
  static values = { phrase: String, toolbarButtonId: String, disabledTitle: String }

  validate () {
    this.continueButtonTarget.disabled = !this.armed
  }

  continue () {
    if (!this.armed) return

    this.disclosureTarget.classList.add('d-none')
    this.confirmationTarget.classList.remove('d-none')
    this.sliderTarget.focus()
  }

  back () {
    this.cancelSpringBack()
    this.settleSlide(0)
    this.confirmationTarget.classList.add('d-none')
    this.disclosureTarget.classList.remove('d-none')
    this.confirmationInputTarget.focus()
  }

  // A handle still being carried has not been put down on an answer, however far over it has reached.
  // The keyboard has no handle to let go of, so there the far end is the answer as it is reached.
  slide () {
    this.paintSlide(this.slideProgress)

    if (this.dragging || this.slideProgress < 1) return

    this.deleting()
  }

  // The track fills in behind the handle, and the label steps out of its way as it comes.
  paintSlide (progress) {
    this.slideTarget.style.setProperty('--staged-deletion-slide-progress', `${progress}`)
    this.slideTarget.style.setProperty('--staged-deletion-slide-label-opacity', `${1 - progress}`)
  }

  // A range input jumps its handle to a click on the track, which hands back the click this control rules out.
  // The keyboard keeps its own way over, since taking it away would leave the deletion unreachable without a mouse.
  startSlide (event) {
    if (!this.pressedHandle(event)) {
      event.preventDefault()
      return
    }

    this.dragging = true

    // The handle has been taken hold of again, so the return it was making is over.
    this.cancelSpringBack()
  }

  // Letting the handle go at the far end is the answer, and letting it go anywhere short of the end is not.
  // A handle carried over and then brought back is no answer either, which is the point of waiting for this.
  releaseSlide () {
    if (this.sliderTarget.disabled) return

    this.dragging = false

    if (this.slideProgress < 1) return this.springBack()

    this.deleting()
  }

  // A handle taken away rather than let go, which answers nothing wherever it had reached.
  cancelSlide () {
    if (this.sliderTarget.disabled) return

    this.dragging = false
    this.springBack()
  }

  // Animated rather than set, so letting go reads as the handle returning rather than as nothing having happened.
  // Anyone who has asked for less motion gets the plain snap.
  springBack () {
    this.cancelSpringBack()

    const from = this.slideProgress
    if (from === 0 || this.prefersReducedMotion) return this.settleSlide(0)

    const startedAt = window.performance.now()

    const step = (now) => {
      const elapsed = Math.min((now - startedAt) / SPRING_BACK_MS, 1)

      // Ease out, so the handle leaves quickly and arrives gently.
      this.settleSlide(from * (1 - elapsed) ** 3)

      if (elapsed < 1) this.springBackFrame = window.requestAnimationFrame(step)
    }

    this.springBackFrame = window.requestAnimationFrame(step)
  }

  cancelSpringBack () {
    if (this.springBackFrame) window.cancelAnimationFrame(this.springBackFrame)

    this.springBackFrame = null
  }

  // Moves the handle without asking whether it has arrived: nothing the spring back does is an answer.
  settleSlide (progress) {
    this.sliderTarget.value = progress * Number(this.sliderTarget.max)
    this.paintSlide(progress)
  }

  /**
   * The button that opened this modal lives outside it, and is left disabled
   * while the deletion runs. Disabling it here rather than in the response to
   * this submit keeps whatever renders it next the last word: a small deletion
   * can finish before the response lands.
   */
  disableToolbarButton () {
    const button = document.getElementById(this.toolbarButtonIdValue)
    if (!button) return

    // A native button needs the property as well, or it is styled dead while still taking clicks.
    if ('disabled' in button) button.disabled = true

    button.classList.add('disabled')
    button.setAttribute('aria-disabled', 'true')
    button.setAttribute('tabindex', '-1')
    button.setAttribute('title', this.disabledTitleValue)

    // Said out loud so whoever else keeps this button leaves a running deletion alone.
    button.dataset.deletionPending = 'true'
  }

  // Enter in the phrase field must not reach the form: typing the phrase arms
  // the first screen, it does not skip the second.
  ignoreEnter (event) {
    if (event.key === 'Enter') event.preventDefault()
  }

  // The slide is spent once it has been carried over, so the handle stays where it landed.
  deleting () {
    this.cancelSpringBack()
    this.sliderTarget.disabled = true
    this.slideTarget.classList.add('staged-deletion__slide--deleting')
    this.slideTarget.style.setProperty('--staged-deletion-slide-label-opacity', '1')
    this.slideLabelTarget.textContent = this.slideTarget.dataset.deletingLabel

    this.formTarget.requestSubmit()
  }

  get armed () {
    return this.confirmationInputTarget.value.trim().toLowerCase() === this.phraseValue.toLowerCase()
  }

  get slideProgress () {
    return Number(this.sliderTarget.value) / Number(this.sliderTarget.max)
  }

  // The press has to land on the handle, give or take its own width.
  pressedHandle (event) {
    const track = this.sliderTarget.getBoundingClientRect()
    const handle = parseFloat(getComputedStyle(this.slideTarget).getPropertyValue('--staged-deletion-slide-handle'))
    const centre = track.left + handle / 2 + this.slideProgress * (track.width - handle)

    return Math.abs(event.clientX - centre) <= handle / 2
  }

  get prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}
